/**
 * Splits an assistant response into markdown and rendered-block segments.
 *
 * Two fences are recognised. ```chart renders as a real chart. ```draft renders
 * as a drafted message with a Send button — a draft was previously printed as
 * prose, which left the reader looking at an email they could not send without
 * typing "ok" and spending another turn.
 *
 * Both fences are scanned in ONE pass. Two independent regexes over the same
 * string would each carry their own lastIndex and interleave their segments out
 * of order as soon as a response contained both.
 *
 * When enabled is false, returns a single markdown segment.
 */

export type DraftChannel = 'email';

export type DraftPayload = {
  channel: DraftChannel;
  /** Recipient address. Absent when the model has not resolved one yet. */
  to?: string;
  to_name?: string;
  subject?: string;
  body: string;
  /** The plan-inbox email this draft answers: it goes out in that email's thread. */
  reply_to_message_id?: number;
};

export type ChartSegment =
  | { type: 'markdown'; content: string }
  | { type: 'chart'; chartPayload: { title?: string; config: any } }
  | { type: 'chart-pending' }
  | { type: 'draft'; draftPayload: DraftPayload }
  | { type: 'draft-pending' };

const FENCE = /```(chart|draft)\s*\n([\s\S]*?)```/g;
const OPENERS = ['```chart', '```draft'] as const;

function parseChart(raw: string): { title?: string; config: any } | null {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.config) {
      return parsed as { title?: string; config: any };
    }
  } catch {
    /* fall through */
  }
  return null;
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

function parseDraft(raw: string): DraftPayload | null {
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;

  // A draft with no body is not a draft. Everything else is optional: the model
  // may not have resolved an address yet, and a card with no Send target still
  // beats the same text buried in a paragraph.
  const body = text(parsed.body);
  if (!body) return null;

  // Only email can actually be sent from here — there is no client-callable SMS
  // route — so an unknown channel falls back to markdown rather than rendering a
  // Send button that cannot send.
  const channel = (text(parsed.channel) || 'email').toLowerCase();
  if (channel !== 'email') return null;

  return {
    channel: 'email',
    to: text(parsed.to) || undefined,
    to_name: text(parsed.to_name) || undefined,
    subject: text(parsed.subject) || undefined,
    body,
    reply_to_message_id: Number.isInteger(parsed.reply_to_message_id) && parsed.reply_to_message_id > 0 ? parsed.reply_to_message_id : undefined,
  };
}

/** The opener of an unterminated fence in `remaining`, or null. */
function pendingFence(remaining: string): 'chart' | 'draft' | null {
  let earliest: { kind: 'chart' | 'draft'; at: number } | null = null;
  for (const opener of OPENERS) {
    const at = remaining.indexOf(opener);
    if (at === -1) continue;
    if (!earliest || at < earliest.at) {
      earliest = { kind: opener.slice(3) as 'chart' | 'draft', at };
    }
  }
  if (!earliest) return null;
  const after = remaining.slice(earliest.at);
  return after.trim().endsWith('```') ? null : earliest.kind;
}

export function parseResponseWithCharts(
  responseText: string,
  enabled: boolean,
  streaming = false
): ChartSegment[] {
  if (!responseText || typeof responseText !== 'string') {
    return [{ type: 'markdown', content: '' }];
  }
  if (!enabled) {
    return [{ type: 'markdown', content: responseText }];
  }

  const segments: ChartSegment[] = [];
  const regex = new RegExp(FENCE.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(responseText)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'markdown', content: responseText.slice(lastIndex, match.index) });
    }
    const kind = match[1];
    const raw = match[2].trim();

    if (kind === 'chart') {
      const chartPayload = parseChart(raw);
      if (chartPayload) segments.push({ type: 'chart', chartPayload });
      else segments.push({ type: 'markdown', content: `\`\`\`chart\n${raw}\n\`\`\`` });
    } else {
      const draftPayload = parseDraft(raw);
      if (draftPayload) segments.push({ type: 'draft', draftPayload });
      else segments.push({ type: 'markdown', content: `\`\`\`draft\n${raw}\n\`\`\`` });
    }
    lastIndex = regex.lastIndex;
  }

  const remaining = responseText.slice(lastIndex);
  if (remaining.length > 0) {
    if (streaming) {
      const pending = pendingFence(remaining);
      if (pending) {
        const at = remaining.indexOf(`\`\`\`${pending}`);
        if (at > 0) segments.push({ type: 'markdown', content: remaining.slice(0, at) });
        segments.push({ type: pending === 'chart' ? 'chart-pending' : 'draft-pending' });
        return segments.length ? segments : [{ type: 'markdown', content: '' }];
      }
    }
    segments.push({ type: 'markdown', content: remaining });
  }

  return segments.length ? segments : [{ type: 'markdown', content: responseText }];
}
